import React, { useCallback } from "react";
import { Card, Dropdown } from "react-bootstrap";
import FalconCardHeader from "components/common/FalconCardHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Workbook } from "exceljs";
import { exportDataGrid, exportPivotGrid } from "devextreme/excel_exporter";
import saveAs from "file-saver";

const ChartCard = ({
  title,
  children,
  className = "",
  chartRef,
  showLabelsOnExport = true,
  dataGridRef = null,
  pivotGridRef = null,
  palette,
  rawDataForExport = null, // If provided, use this data for export instead of the chart's dataSource
}) => {
  // Datagrid export to excel
  const handleDataGridExport = useCallback(() => {
    if (!dataGridRef?.current) return;

    const component = dataGridRef.current.instance();
    component.beginUpdate();
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(title);

    exportDataGrid({
      component: component,
      worksheet: worksheet,
      autoFilterEnabled: true,
    })
      .then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            `${title.replace(/\s+/g, "_")}.xlsx`
          );
        });
      })
      .then(() => {
        component.endUpdate();
      });
  }, [dataGridRef, title]);

  // PivotGrid export to excel
  const handlePivotGridExport = useCallback(() => {
    if (!pivotGridRef?.current) return;

    const component = pivotGridRef.current.instance();
    component.beginUpdate();
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(title);

    exportPivotGrid({
      component: component,
      worksheet: worksheet,
      autoFilterEnabled: true,
      autoColumnWidth: true,
      customizeCell: (options) => {
        const { pivotCell, excelCell } = options;

        if (!pivotCell) return;

        // Column headers
        if (pivotCell.type === "D" && pivotCell.area === "column") {
          excelCell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
            size: 12,
          };
          excelCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: palette?.[0]?.replace("#", "") || "2B5895" },
          };
          excelCell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
        }

        // Column subheaders
        if (pivotCell.type === "T" && pivotCell.area === "column") {
          excelCell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
            size: 11,
          };
          excelCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: palette?.[1]?.replace("#", "") || "2B5895" },
          };
          excelCell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
        }

        // Column grand totals
        if (pivotCell.type === "GT" && pivotCell.area === "column") {
          excelCell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
            size: 11,
          };
          excelCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: palette?.[1]?.replace("#", "") || "2B5895" },
          };
          excelCell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
        }

        // Row headers
        if (pivotCell.type === "D" && pivotCell.area === "row") {
          excelCell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
            size: 12,
          };
          excelCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: palette?.[0]?.replace("#", "") || "2B5895" },
          };
          excelCell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
        }

        // Row subheaders
        if (pivotCell.type === "T" && pivotCell.area === "row") {
          excelCell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
            size: 11,
          };
          excelCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: palette?.[1]?.replace("#", "") || "2B5895" },
          };
          excelCell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
        }

        // Row grand totals
        if (pivotCell.type === "GT" && pivotCell.area === "row") {
          excelCell.font = {
            bold: true,
            color: { argb: "FFFFFFFF" },
            size: 11,
          };
          excelCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: palette?.[1]?.replace("#", "") || "2B5895" },
          };
          excelCell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
        }

        // Data cells
        if (pivotCell.type === "D" && pivotCell.area === "data") {
          excelCell.alignment = {
            vertical: "middle",
            horizontal: "right",
          };
        }

        // Borders
        excelCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      },
    })
      .then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            `${title.replace(/\s+/g, "_")}PivotGrid.xlsx`
          );
        });
      })
      .then(() => {
        component.endUpdate();
      });
  }, [pivotGridRef, title, palette]);

  // Helper function to detect if a value is numeric
  const isNumericValue = (value) => {
    if (value === null || value === undefined || value === "") return false;
    if (typeof value === "number") return true;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return (
        /^-?\d+(\.\d+)?$/.test(trimmed) ||
        /^-?\d{1,3}([,\s]\d{3})*(\.\d+)?$/.test(trimmed)
      );
    }
    return false;
  };

  // Helper function to parse numeric value (handles strings with separators)
  const parseNumericValue = (value) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      return parseFloat(value.replace(/[,\s]/g, ""));
    }
    return 0;
  };

  // Helper function to detect if a column contains currency values
  const isCurrencyColumn = (header, dataSource) => {
    const lowerHeader = header.toLowerCase();
    const currencyKeywords = [
      "amount",
      "value",
      "price",
      "cost",
      "revenue",
      "discount",
      "amortization",
      "ecl",
      "gap",
    ];
    return currencyKeywords.some((keyword) => lowerHeader.includes(keyword));
  };

  // Helper function to detect if a column contains percentage values
  const isPercentageColumn = (header, dataSource) => {
    const lowerHeader = header.toLowerCase();
    const percentageKeywords = ["percentage", "percent", "pct", "%"];
    return percentageKeywords.some((keyword) => lowerHeader.includes(keyword));
  };

  // Helper function to detect if a column contains only integer values
  const isIntegerColumn = (header, dataSource) => {
    const sampleValues = dataSource
      .map((item) => item[header])
      .filter((val) => val !== null && val !== undefined && val !== "");

    if (sampleValues.length === 0) return false;

    return sampleValues.every((val) => {
      const numValue = parseNumericValue(val);
      return !isNaN(numValue) && Number.isInteger(numValue);
    });
  };

  // Chart export to excel
  const handleExportDataToExcel = useCallback(() => {
    if (!chartRef?.current?.instance) return;

    const instance = chartRef.current.instance();
    const dataSource = rawDataForExport || instance.option("dataSource");

    if (!dataSource || dataSource.length === 0) return;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(title);

    // Add headers
    const headers = Object.keys(dataSource[0] || {});
    const headerRow = worksheet.addRow(
      headers.map((header) =>
        header
          .split(/(?=[A-Z])|[_\s]+/)
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(" ")
      )
    );

    // Style headers
    headerRow.eachCell((cell) => {
      cell.font = {
        bold: true,
        color: { argb: "FFFFFFFF" },
        size: 12,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: palette?.[0]?.replace("#", "") || "2B5895" },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Detect column types
    const columnTypes = headers.map((header) => {
      const sampleValues = dataSource
        .map((item) => item[header])
        .filter((val) => val !== null && val !== undefined && val !== "");

      if (sampleValues.length === 0) return "text";

      const allNumeric = sampleValues.every((val) => isNumericValue(val));
      if (allNumeric) {
        if (isPercentageColumn(header, dataSource)) {
          return "percentage";
        }
        if (isCurrencyColumn(header, dataSource)) {
          return "currency";
        }
        // Check if all values are integers (for counters, line numbers, etc.)
        if (isIntegerColumn(header, dataSource)) {
          return "integer";
        }
        return "number";
      }
      return "text";
    });

    // Detect percentage format (decimal 0-1 or standard 0-100) for each percentage column
    const percentageFormats = headers.map((header, colIndex) => {
      if (columnTypes[colIndex] === "percentage") {
        const sampleValues = dataSource
          .map((item) => {
            const val = item[header];
            return val !== null && val !== undefined && val !== ""
              ? parseNumericValue(val)
              : null;
          })
          .filter((val) => val !== null && !isNaN(val));

        if (sampleValues.length > 0) {
          // If all values are <= 1, assume decimal format (0-1), otherwise standard (0-100)
          const maxValue = Math.max(...sampleValues.map(Math.abs));
          return maxValue <= 1 ? "decimal" : "standard";
        }
      }
      return null;
    });

    // Add data with proper formatting
    dataSource.forEach((item) => {
      const row = worksheet.addRow(
        headers.map((header, colIndex) => {
          const value = item[header];
          const columnType = columnTypes[colIndex];

          if (
            columnType === "number" ||
            columnType === "currency" ||
            columnType === "integer"
          ) {
            const numValue = parseNumericValue(value);
            return isNaN(numValue) ? value : numValue;
          } else if (columnType === "percentage") {
            const numValue = parseNumericValue(value);
            if (isNaN(numValue)) return value;
            // Convert to decimal format (0-1) if in standard format (0-100)
            const percentageFormat = percentageFormats[colIndex];
            return percentageFormat === "standard" ? numValue / 100 : numValue;
          }
          return value;
        })
      );

      // Style data cells with proper formatting
      row.eachCell((cell, colNumber) => {
        const colIndex = colNumber - 1;
        const columnType = columnTypes[colIndex];
        const header = headers[colIndex];

        // Set alignment
        if (
          columnType === "number" ||
          columnType === "currency" ||
          columnType === "percentage" ||
          columnType === "integer"
        ) {
          cell.alignment = {
            vertical: "middle",
            horizontal: "right",
          };
        } else {
          cell.alignment = {
            vertical: "middle",
            horizontal: "left",
          };
        }

        // Apply number formatting
        if (columnType === "currency") {
          // Format as currency with thousands separator
          cell.numFmt = '#,##0.00" €"';
        } else if (columnType === "percentage") {
          // Format as percentage with 2 decimal places
          cell.numFmt = "0.00%";
        } else if (columnType === "integer") {
          // Format as integer with thousands separator, no decimals
          cell.numFmt = "#,##0";
        } else if (columnType === "number") {
          // Format as number with thousands separator
          cell.numFmt = "#,##0.00";
        }

        // Apply borders
        cell.border = {
          top: { style: "thin", color: { argb: "DDDDDD" } },
          left: { style: "thin", color: { argb: "DDDDDD" } },
          bottom: { style: "thin", color: { argb: "DDDDDD" } },
          right: { style: "thin", color: { argb: "DDDDDD" } },
        };
      });
    });

    // Adjust columns width
    headers.forEach((header, i) => {
      const column = worksheet.getColumn(i + 1);
      const columnType = columnTypes[i];

      // Calculate width based on content
      let maxLength = header.length;
      dataSource.forEach((item) => {
        const value = item[header];
        let displayLength = 0;

        if (columnType === "number" || columnType === "currency") {
          const numValue = parseNumericValue(value);
          if (!isNaN(numValue)) {
            // Estimate length with formatting (thousands separator)
            const formatted = new Intl.NumberFormat("fr-FR", {
              minimumFractionDigits: columnType === "currency" ? 2 : 0,
              maximumFractionDigits: 2,
            }).format(numValue);
            displayLength =
              formatted.length + (columnType === "currency" ? 3 : 0); // +3 for " €"
          } else {
            displayLength = String(value || "").length;
          }
        } else if (columnType === "integer") {
          const numValue = parseNumericValue(value);
          if (!isNaN(numValue)) {
            // Format as integer with thousands separator, no decimals
            const formatted = new Intl.NumberFormat("fr-FR", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(numValue);
            displayLength = formatted.length;
          } else {
            displayLength = String(value || "").length;
          }
        } else if (columnType === "percentage") {
          const numValue = parseNumericValue(value);
          if (!isNaN(numValue)) {
            // Convert to decimal if needed for formatting
            const percentageFormat = percentageFormats[i];
            const decimalValue =
              percentageFormat === "standard" ? numValue / 100 : numValue;
            // Format as percentage and estimate length (e.g., "12.34%")
            const formatted = new Intl.NumberFormat("fr-FR", {
              style: "percent",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(decimalValue);
            displayLength = formatted.length;
          } else {
            displayLength = String(value || "").length;
          }
        } else {
          displayLength = String(value || "").length;
        }

        maxLength = Math.max(maxLength, displayLength);
      });

      column.width = Math.max(header.length + 5, Math.min(50, maxLength + 2));
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        `${title.replace(/\s+/g, "_")}_data.xlsx`
      );
    });
  }, [chartRef, title, palette, rawDataForExport]);

  // Helper functions for chart export
  const getLabelFormatter = (serieName) => {
    const lowerName = serieName.toLowerCase();
    const isPercentage =
      lowerName.includes("percentage") || lowerName.includes("percent");
    const isNumber = lowerName.includes("number");

    if (isPercentage && !isNumber) {
      return (pointInfo) => {
        const roundedValue = Math.round(pointInfo.value * 10) / 10;
        if (roundedValue === Math.floor(roundedValue)) {
          return `${Math.floor(roundedValue)}%`;
        }
        return `${roundedValue.toFixed(1).replace(".", ",")}%`;
      };
    }

    if (isNumber) {
      return null; // Will use format option instead
    }

    return (pointInfo) => {
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return formatter.format(pointInfo.value);
    };
  };

  const configureSeriesLabelsForExport = (instance, seriesOptions) => {
    const series = Array.isArray(seriesOptions)
      ? seriesOptions
      : [seriesOptions];
    const chartType = instance.NAME;

    const dataSource = instance.option("dataSource") || [];
    const dataPointCount = Array.isArray(dataSource) ? dataSource.length : 0;

    if (chartType === "dxPieChart" || chartType === "dxChart") {
      instance.option("resolveLabelOverlapping", "stack");
    }

    series.forEach((serie, index) => {
      instance.option(`series[${index}].label.visible`, true);

      const serieName = serie.name || "";
      const formatter = getLabelFormatter(serieName);
      const isNumber = serieName.toLowerCase().includes("number");

      if (formatter) {
        instance.option(`series[${index}].label.customizeText`, formatter);
        instance.option(`series[${index}].label.format`, "");
      } else if (isNumber) {
        instance.option(`series[${index}].label.format`, { precision: 0 });
      }

      if (chartType === "dxPieChart") {
        instance.option(`series[${index}].label.connector`, {
          visible: false,
          width: 1,
        });

        instance.option(`series[${index}].label.position`, "outside");

        if (dataPointCount > 8) {
          instance.option(`series[${index}].label.radialOffset`, 20);
          instance.option(`series[${index}].label.font.size`, 8);
        } else {
          instance.option(`series[${index}].label.radialOffset`, 10);
          instance.option(`series[${index}].label.font.size`, 9);
        }
      } else {
        const isStacked =
          serie.stack ||
          instance.option("commonSeriesSettings.type")?.includes("stacked");

        if (isStacked) {
          instance.option(`series[${index}].label.position`, "inside");
        } else {
          instance.option(`series[${index}].label.position`, "outside");
        }

        if (isStacked) {
          instance.option(`series[${index}].label.font.size`, 8);
        } else if (dataPointCount > 15) {
          instance.option(`series[${index}].label.font.size`, 9);
        } else {
          instance.option(`series[${index}].label.font.size`, 10);
        }
      }
    });
  };

  const configureTitleForExport = (instance, chartTitle) => {
    instance.option("title", {
      text: chartTitle,
      verticalAlignment: "top",
      horizontalAlignment: "center",
      font: { size: 14 },
    });
  };

  const handleExport = React.useCallback(
    async (format) => {
      if (!chartRef?.current?.instance) return;

      const instance = chartRef.current.instance();
      const fileName = title.replace(/\s+/g, "_");

      const currentTitleOptions = instance.option("title");
      let currentSeriesOptions = null;
      let currentLayersOptions = null;


      try {
        if (
          (instance.NAME === "dxPieChart" || instance.NAME === "dxChart") &&
          showLabelsOnExport
        ) {
          if (instance.option("series")) {
            currentSeriesOptions = JSON.parse(
              JSON.stringify(instance.option("series"))
            );
            configureSeriesLabelsForExport(instance, currentSeriesOptions);
          }
        } else if (instance.NAME === "dxVectorMap") {
          currentLayersOptions = instance.option("layers");
        }

        if (instance.NAME !== "dxVectorMap") {
          configureTitleForExport(instance, title);
        }

        await new Promise((resolve) => setTimeout(resolve, 1500));

        const exportResult = instance.exportTo(fileName, format);
        await Promise.resolve(exportResult);
      } catch (error) {
        console.error("Erreur lors de l'export du graphique:", error);
        console.error("Détails:", error.stack);
      } finally {
        instance.option("title", currentTitleOptions);
        if (currentSeriesOptions) {
          instance.option("series", currentSeriesOptions);
        }
        if (currentLayersOptions != null) {
          instance.option("layers", currentLayersOptions);
        }
      }
    },
    [chartRef, title, showLabelsOnExport]
  );

  const ChartDropdown = () => (
    <Dropdown
      align="end"
      className="font-sans-serif btn-reveal-trigger"
      drop="down"
    >
      {chartRef && (
        <>
          <Dropdown.Toggle variant="reveal" size="sm">
            <FontAwesomeIcon icon="ellipsis-h" className="fs-11" />
          </Dropdown.Toggle>
          <Dropdown.Menu className="border py-2">
            <Dropdown.Header className="dx-font text-uppercase text-600 fs-11">
              Export as Image
            </Dropdown.Header>
            <Dropdown.Item
              className="dx-font"
              onClick={() => handleExport("PNG")}
            >
              PNG Image
            </Dropdown.Item>
            <Dropdown.Item
              className="dx-font"
              onClick={() => handleExport("PDF")}
            >
              PDF Document
            </Dropdown.Item>
            <Dropdown.Item
              className="dx-font"
              onClick={() => handleExport("JPEG")}
            >
              JPEG Image
            </Dropdown.Item>
            <Dropdown.Item
              className="dx-font"
              onClick={() => handleExport("SVG")}
            >
              SVG Image
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Header className="dx-font text-uppercase text-600 fs-11">
              Export Data
            </Dropdown.Header>
            <Dropdown.Item
              className="dx-font"
              onClick={() => handleExportDataToExcel()}
            >
              Excel Document
            </Dropdown.Item>
          </Dropdown.Menu>
        </>
      )}
    </Dropdown>
  );

  const DataGridDropdown = () => (
    <Dropdown
      align="end"
      className="font-sans-serif btn-reveal-trigger"
      drop="down"
    >
      <Dropdown.Toggle variant="reveal" size="sm">
        <FontAwesomeIcon icon="ellipsis-h" className="fs-11" />
      </Dropdown.Toggle>
      <Dropdown.Menu className="border py-2">
        <Dropdown.Item className="dx-font" onClick={handleDataGridExport}>
          Export to Excel
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  const PivotGridDropdown = () => (
    <Dropdown
      align="end"
      className="font-sans-serif btn-reveal-trigger"
      drop="down"
    >
      <Dropdown.Toggle variant="reveal" size="sm">
        <FontAwesomeIcon icon="ellipsis-h" className="fs-11" />
      </Dropdown.Toggle>
      <Dropdown.Menu className="border py-2">
        <Dropdown.Item className="dx-font" onClick={handlePivotGridExport}>
          Export to Excel
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <Card className="p-0">
      <FalconCardHeader
        title={title}
        titleTag="h6"
        className="py-2 dx-font"
        titleClass="fs-0"
        endEl={
          pivotGridRef ? (
            <PivotGridDropdown />
          ) : dataGridRef ? (
            <DataGridDropdown />
          ) : (
            <ChartDropdown />
          )
        }
      />
      <Card.Body className={className || "p-0"}>{children}</Card.Body>
    </Card>
  );
};

export default ChartCard;
